import { Trans, useLingui } from "@lingui/react/macro";
import { Search } from "lucide-react";
import { useState } from "react";

import type { DataTableInstance } from "../table-features";
import type { FilterField } from "./types";

import { cn } from "../../../../../utils/shadcn-ui";
import { Button } from "../../../button";
import { BoolEditor } from "./editors/bool-editor";
import { MultiEditor } from "./editors/multi-editor";
import { OperatorSelect } from "./editors/operator-select";
import { TextEditor } from "./editors/text-editor";
import { clearField, getMultiSelected } from "./tanstack-bridge";

/**
 * Label row + editor for a single filter field. Shared between the global
 * filter dialog and the per-column header filter popover.
 */
export function FilterFieldEditor<T>({
  table,
  field,
  autoFocusText,
  onSubmit,
}: {
  table: DataTableInstance<T>;
  field: FilterField<T>;
  /** Autofocus the text input on mount (text fields only). */
  autoFocusText?: boolean;
  /** Called when a text field is submitted with Enter. */
  onSubmit?: () => void;
}) {
  const { t } = useLingui();
  const [searchOpen, setSearchOpen] = useState(false);
  const isDisabled =
    field.kind === "multi" && (field.disabled?.(table) ?? false);

  return (
    <div className="px-1.5 pb-1.5">
      <div className="flex items-center justify-between gap-2 px-1.5 pt-1 pb-0.5 min-h-6">
        <div
          className={cn(
            "text-xs font-medium",
            isDisabled ? "text-muted-foreground/50" : "text-muted-foreground",
          )}
        >
          {field.label}
        </div>
        {field.kind === "text" && (
          <OperatorSelect table={table} field={field} />
        )}
        {field.kind === "multi" && !isDisabled && (
          <div className="flex items-center gap-1.5">
            {getMultiSelected(table, field).length > 0 && (
              <button
                type="button"
                onClick={() => clearField(table, field)}
                className="bg-transparent border-0 p-0 leading-none text-xs font-semibold text-muted-foreground hover:text-brand-primary underline underline-offset-2 cursor-pointer shrink-0"
              >
                <Trans>Clear selection</Trans>
              </button>
            )}
            <Button
              variant="tertiary"
              size="xs"
              icon={Search}
              label={t`Search options`}
              aria-pressed={searchOpen}
              onClick={() => setSearchOpen((open) => !open)}
            />
          </div>
        )}
      </div>
      {field.kind === "text" && (
        <TextEditor
          table={table}
          field={field}
          autoFocus={autoFocusText}
          onSubmit={onSubmit}
        />
      )}
      {field.kind === "multi" && !isDisabled && (
        <MultiEditor table={table} field={field} showSearch={searchOpen} />
      )}
      {field.kind === "multi" && isDisabled && (
        <div className="px-3 py-1.5 text-xs text-muted-foreground/50 italic">
          {field.disabledMessage ?? t`Not available yet`}
        </div>
      )}
      {field.kind === "bool" && (
        <div className="px-3 py-1.5">
          <BoolEditor table={table} field={field} />
        </div>
      )}
    </div>
  );
}
